import { Toolbar } from '@mui/material';
import { computed } from 'mobx';

import React, { PureComponent } from 'react';

import BackButton from '../Button/BackButton';
import RefreshDataButton from '../Button/RefreshDataButton';
import ToolboxButton from '../Button/ToolboxButton';
import FlexRow from '../Flex/FlexRow';
import Spacer from '../Spacer/Spacer';
import cardHeaderStyle from './cardStyles/cardHeaderStyle';
import type ViewStore from 'stores/ViewStore';
import type AppLayoutStore from 'stores/AppLayoutStore';
import type { RouterStore } from 'stores/RouterStore';
import type ThemeStore from 'stores/ThemeStore';
import injectStores from '../RootStore/injectStores';

import { styled } from '@mui/material/styles';

interface CardHeaderDivProps {
  theme?: {
    palette: {
      [key: string]: {
        main: string;
      };
    };
    spacing: (arg0: number) => string;
    typography: {
      body2: {
        fontFamily: string;
        fontSize: string;
        fontWeight: string;
        lineHeight: string;
      };
    };
  };
  color?: string;
  plain?: boolean;
  image?: boolean;
  stats?: boolean;
  icon?: boolean;
  text?: boolean;
  isMobile?: boolean;
  toolbar?: boolean;
}

const CardHeaderDiv = styled('div')<CardHeaderDivProps>(({
  theme,
  color,
  plain,
  image,
  stats,
  icon,
  text,
  isMobile,
  toolbar,
}) => {
  return {
    // ...theme.typography.body2,
    ...(color && { backgroundColor: theme.palette[color].main }),
    ...(plain && { boxShadow: 'none' }),
    ...(image && { backgroundSize: 'cover' }),
    ...(stats && { display: 'flex', alignItems: 'center' }),
    ...(icon && { display: 'flex', alignItems: 'center' }),
    ...(text && { textAlign: 'center' }),
    ...(isMobile
      ? { height: '45px', paddingLeft: '8px', paddingRight: '8px' }
      : { height: '67px', paddingRight: '15px', paddingLeft: '15px' }),
    ...(toolbar && { padding: theme.spacing(1) }),
  };
});

interface CardHeaderProps {
  classes?: {
    cardHeader: string;
    [key: string]: string;
  };
  color?:
  | 'primary'
  | 'secondary'
  | 'warning'
  | 'success'
  | 'danger'
  | 'info'
  | 'rose'
  | 'white'
  | 'gray';
  plain?: boolean;
  image?: boolean;
  stats?: boolean;
  icon?: boolean;
  text?: boolean;
  standardHeight?: boolean;
  enableBackButton?: boolean;
  enableRefreshButton?: boolean;
  refreshButtonLoading?: boolean;
  enableToolbox?: boolean;
  children?: React.ReactNode;
  title?: string;
  center?: boolean;
  viewStore?: ViewStore;
  appLayoutStore?: AppLayoutStore;
  routerStore?: RouterStore;
  toolbar?: boolean;
  cardHeight?: string;
  buttonSize?: string;
  themeStore?: ThemeStore;
}

class CardHeaderBase extends PureComponent<CardHeaderProps> {
  render() {
    const {
      viewStore,
      appLayoutStore,
      children,
      color = 'primary',
      toolbar = true,
      enableBackButton = false,
      enableToolbox = false,
      enableRefreshButton = false,
      refreshButtonLoading = false,
      routerStore,
      title,
      center = false,
      ...rest
    } = this.props;

    const isMobile = viewStore?.isMobile ?? false;

    const enableToolboxButton = computed(
      () =>
        enableToolbox &&
        (appLayoutStore?.toolboxRoutes?.length ?? 0) > 0 &&
        appLayoutStore?.isRightMenuSelection
    ).get();

    if (toolbar) {
      return (
        <CardHeaderDiv
          isMobile={isMobile}
          color={color}
          style={{
            paddingLeft: '8px',
            paddingRight: '8px',
            borderRadius: '8px',
          }}
        // {...this.props}
        >
          <Toolbar
            disableGutters={true}
            variant="dense"
            style={{
              height: isMobile ? '45px' : '67px',
              justifyContent: center ? 'center' : 'normal',
            }}
          >
            <FlexRow style={{ paddingRight: '5px' }}>
              {enableBackButton && <BackButton />}
              {enableToolboxButton && <ToolboxButton />}
            </FlexRow>
            {title && (
              <h3
                style={{
                  color: 'white',
                  paddingLeft:
                    enableBackButton ||
                      enableRefreshButton ||
                      enableToolboxButton
                      ? '0px'
                      : '8px',
                  fontSize: isMobile ? '0.9rem' : '1.0rem',
                  lineHeight: isMobile ? '1.2rem' : '1.4rem',
                }}
              >
                {title}
              </h3>
            )}
            {children}
            {enableRefreshButton && routerStore && (
              <>
                {!children && <Spacer />}
                <RefreshDataButton
                  refreshButtonLoading={refreshButtonLoading}
                />
              </>
            )}
          </Toolbar>
        </CardHeaderDiv>
      );
    }

    return (
      <CardHeaderDiv isMobile={isMobile} color={color} {...rest}>
        <h3 style={{ color: 'white' }}>{title}</h3>
        {children}
      </CardHeaderDiv>
    );
  }
}

const CardHeader = styled(injectStores(CardHeaderBase))(cardHeaderStyle);

export default CardHeader;
